// Let's keep the file self-contained as it was, but fix the logic.

/**
 * Gematria Mapping (Abjad numerals)
 * Standard Jewish Gematria values for Hebrew letters.
 * Used for converting text to numerical values.
 */
export const GEMATRIA_MAP: Record<string, number> = {
  'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5, 'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9,
  'י': 10, 'כ': 20, 'ל': 30, 'מ': 40, 'נ': 50, 'ס': 60, 'ע': 70, 'פ': 80,
  'צ': 90, 'ק': 100, 'ר': 200, 'ש': 300, 'ת': 400,
  'ך': 20, 'ם': 40, 'ן': 50, 'ף': 80, 'ץ': 90
};

/**
 * Zodiac Signs Mapping
 * Maps numerical indices (1-12) to Hebrew Zodiac sign names.
 * 1 = Aries (Tale), ..., 12 = Pisces (Dagim).
 */
export const SIGNS_MAP: Record<number, string> = {
  1: 'טלה', 2: 'שור', 3: 'תאומים', 4: 'סרטן',
  5: 'אריה', 6: 'בתולה', 7: 'מאזניים', 8: 'עקרב',
  9: 'קשת', 10: 'גדי', 11: 'דלי', 12: 'דגים', 0: 'דגים'
};

/**
 * Planets Mapping
 * Maps planet keys to Hebrew names.
 */
export const PLANETS_MAP: Record<string, string> = {
  sun: 'שמש',
  moon: 'ירח',
  mercury: 'חמה',
  venus: 'נוגה',
  mars: 'מאדים',
  jupiter: 'צדק',
  saturn: 'שבתאי',
  uranus: 'אורנוס',
  neptune: 'נפטון',
  pluto: 'פלוטו',
  trueNode: 'ראש דרקון',
  chiron: 'כירון',
  lilith: 'לילית',
  juno: 'ג\'ונו',
  ceres: 'סרס',
  pallas: 'פאלאס',
  vesta: 'וסטה',
  ascendant: 'אופק',
  mc: 'רום שמיים',
  midheaven: 'רום שמיים',
  vertex: 'ורטקס',
  fortune: 'נקודת המזל'
};

/**
 * Common Hebrew labels for numerology fields
 */
export const NUMEROLOGY_LABELS: Record<string, string> = {
  firstNumber: 'שם פרטי',
  familyNumber: 'שם משפחה',
  totalNameNumber: 'שם מלא',
  eheviNumber: 'אהו"י',
  itzurimNumber: 'עיצורים',
  hamashaOuterSum: 'שם מלא + תאריך'
};

/**
 * Numerology Mapping (Mispar Katan)
 * Reduces Gematria values to single digits (1-9).
 * E.g., Aleph (1) -> 1, Yod (10) -> 1, Kuf (100) -> 1.
 */
export const NUMEROLOGY_MAP: Record<string, number> = Object.fromEntries(
  Object.entries(GEMATRIA_MAP).map(([char, value]) => [char, parseInt(value.toString()[0])])
);

/**
 * Calculates the full Gematria value of a string.
 * Sums the standard Gematria values of each valid Hebrew character.
 * @param text The input text (Hebrew)
 * @returns Total Gematria sum
 */
export const calculateGematriaValue = (text: string): number => {
  return text.split('').reduce((sum, char) => {
    return sum + (GEMATRIA_MAP[char] || 0);
  }, 0);
};

/**
 * Calculates the Numerological value (Mispar Katan) of a string.
 * Uses the reduced digit values for calculation.
 * @param text The input text (Hebrew)
 * @returns Total Numerological sum
 */
export const calculateNumerologicalValue = (text: string): number => {
  return text.split('').reduce((sum, char) => {
    return sum + (NUMEROLOGY_MAP[char] || 0);
  }, 0);
};

/**
 * Calculates the 'Ehevi' value (Vowels/Soul).
 * Considers only the letters Aleph (א), He (ה), Vav (ו), Yod (י).
 * Represents the "Soul" or inner motivation in Kabbalistic Numerology.
 * @param text The input text
 * @returns Sum of Ehevi letters
 */
export const calculateEhevi = (text: string): number => {
  return text.split('').reduce((sum, char) => {
    return sum + (char === 'א' || char === 'ה' || char === 'ו' || char === 'י' ? NUMEROLOGY_MAP[char] || 0 : 0);
  }, 0);
};

/**
 * Reduces a number to a single digit by iteratively summing its digits.
 * Standard numerological reduction (e.g., 56 -> 11 -> 2).
 * NOTE: This implementation does NOT preserve Master Numbers (11, 22) during intermediate steps,
 * it reduces strictly to a single digit 1-9.
 * @param num Input number
 * @returns Single digit 1-9
 */
export const sumDigits = (num: number): number => {
  if (num === undefined || num === null || isNaN(num)) return 0;
  let result = Math.abs(Math.floor(num));
  while (result >= 10) {
    result = result.toString().split('').reduce((sum, digit) => sum + (parseInt(digit) || 0), 0);
  }
  return result;
};

/**
 * Calculates the "Inner Sign" (Mazal Pnimi) based on birth date.
 * Now uses UTC normalization to ensure sync with astronomical data.
 * 
 * Logic:
 * Aries (1): Mar 21 - Apr 20
 * Taurus (2): Apr 21 - May 20
 * ...
 * Pisces (12): Feb 19 - Mar 20
 * 
 * @param dateStr Date in 'dd/mm/yyyy' or 'yyyy-mm-dd' format
 * @param birthTime Optional birth time (HH:mm)
 * @param timezone Optional timezone string (IANA)
 * @param preciseLongitude Optional astronomical longitude for 100% sync
 * @returns Object containing the sign name, number (1-12), and calculated age
 */
export const calculateZodiacSign = (dateStr: string, birthTime?: string, timezone?: string, preciseLongitude?: number) => {
  // Validate input
  if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') {
    return { sign: '?', number: 0, age: 0 };
  }

  // 1. Parsing Input Date
  let day: number, month: number, year: number;
  const parts = dateStr.split(/[/.-]/);

  if (parts.length === 3) {
    const p0 = parseInt(parts[0]);
    if (p0 > 31) {
      year = p0;
      month = parseInt(parts[1]);
      day = parseInt(parts[2]);
    } else {
      day = p0;
      month = parseInt(parts[1]);
      year = parseInt(parts[2]);
    }
  } else {
    return { sign: '?', number: 0, age: 0 };
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return { sign: '?', number: 0, age: 0 };
  }

  // Basic validation
  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return { sign: '?', number: 0, age: 0 };
  }

  // 2. Preparation for UTC Sync
  // Use 00:00:00 as default if time is missing to ensure consistency across the application
  const time = birthTime || "00:00";
  const [hours, minutes] = time.split(':').map(Number);

  // Use user's current timezone as fallback if missing
  const activeTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  let utcDate: Date;
  try {
    // Construct local reference date
    const localDate = new Date(year, month - 1, day, hours, minutes);

    // Get offset for this specific moment in the target timezone
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: activeTimezone,
      timeZoneName: "shortOffset",
    });
    const offsetPart = formatter.formatToParts(localDate).find(p => p.type === "timeZoneName")?.value || "GMT+0";

    // Simple offset parsing (e.g., GMT+2 or GMT-05:00)
    let offset = offsetPart.replace("GMT", "");
    if (!offset || offset === "") offset = "+00:00";
    if (!offset.includes(":")) {
      const isNegative = offset.startsWith("-");
      const absValue = offset.replace(/[+-]/, "").padStart(2, '0');
      offset = `${isNegative ? '-' : '+'}${absValue}:00`;
    }

    // Construct ISO string with offset for precise UTC conversion
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const hoursStr = String(hours).padStart(2, '0');
    const minStr = String(minutes).padStart(2, '0');

    const isoStr = `${year}-${monthStr}-${dayStr}T${hoursStr}:${minStr}:00${offset}`;
    utcDate = new Date(isoStr);

    // Fallback if ISO parsing fails
    if (isNaN(utcDate.getTime())) {
      utcDate = localDate;
    }
  } catch (_e) {
    utcDate = new Date(year, month - 1, day, 0, 0);
  }

  // 3. Normalized Data (UTC based)
  const utcDay = utcDate.getUTCDate();
  const utcMonth = utcDate.getUTCMonth() + 1; // 1-12

  // 4. Age Calculation (Precise)
  const now = new Date();
  let calculatedAge = now.getUTCFullYear() - year;
  const m = now.getUTCMonth() - (utcMonth - 1);
  if (m < 0 || (m === 0 && now.getUTCDate() < utcDay)) {
    calculatedAge--;
  }
  const age = Math.max(0, calculatedAge);

  // 5. Zodiac Determination
  let signNumber: number;

  if (preciseLongitude !== undefined) {
    // If we have astronomical data, use it as the source of truth
    // 0-29.99 = Aries (1), 30-59.99 = Taurus (2), etc.
    signNumber = (Math.floor(preciseLongitude / 30) % 12) + 1;
  } else {
    // Fallback to UTC-normalized calculation based on date/time
    if ((utcMonth === 12 && utcDay >= 22) || (utcMonth === 1 && utcDay <= 20)) {
      signNumber = 10; // גדי
    } else if ((utcMonth === 1 && utcDay >= 21) || (utcMonth === 2 && utcDay <= 18)) {
      signNumber = 11; // דלי
    } else if ((utcMonth === 2 && utcDay >= 19) || (utcMonth === 3 && utcDay <= 20)) {
      signNumber = 12; // דגים
    } else if ((utcMonth === 3 && utcDay >= 21) || (utcMonth === 4 && utcDay <= 20)) {
      signNumber = 1; // טלה
    } else if ((utcMonth === 4 && utcDay >= 21) || (utcMonth === 5 && utcDay <= 20)) {
      signNumber = 2; // שור
    } else if ((utcMonth === 5 && utcDay >= 21) || (utcMonth === 6 && utcDay <= 21)) {
      signNumber = 3; // תאומים
    } else if ((utcMonth === 6 && utcDay >= 22) || (utcMonth === 7 && utcDay <= 22)) {
      signNumber = 4; // סרטן
    } else if ((utcMonth === 7 && utcDay >= 23) || (utcMonth === 8 && utcDay <= 22)) {
      signNumber = 5; // אריה
    } else if ((utcMonth === 8 && utcDay >= 23) || (utcMonth === 9 && utcDay <= 22)) {
      signNumber = 6; // בתולה
    } else if ((utcMonth === 9 && utcDay >= 23) || (utcMonth === 10 && utcDay <= 23)) {
      signNumber = 7; // מאזניים
    } else if ((utcMonth === 10 && utcDay >= 24) || (utcMonth === 11 && utcDay <= 21)) {
      signNumber = 8; // עקרב
    } else {
      signNumber = 9; // קשת
    }
  }

  return {
    sign: SIGNS_MAP[signNumber],
    number: signNumber,
    age
  };
};

/**
 * Calculates the "Surrounding Sign" (Makif) based on the First Name.
 * Logic:
 * 1. Calculate full Gematria of the name.
 * 2. Modulo 12 of the result.
 * 3. 0 is treated as 12.
 * 
 * @param name The person's first name
 * @returns Object with sign name, number (1-12), and original gematria value
 */
export const calculateGematria = (name: string) => {
  const gematria = calculateGematriaValue(name);
  const signNumber = gematria % 12 || 12;

  return {
    sign: SIGNS_MAP[signNumber],
    number: signNumber,
    gematria
  };
};

/**
 * Performs full Numerological Analysis (The "Tur").
 * Calculates various sums and reductions based on name and birth date.
 * 
 * Key Calculations:
 * - Day/Month/Year Numbers: Proper sums of date parts.
 * - Total Name Number: First + Family (reduced).
 * - Ehevi (Soul): Vowels only (reduced).
 * - Itzurim (Body): Consonants only (Total - Ehevi).
 * - Hamasha Inner Sum: Reduced Name + Reduced Date.
 * 
 * @param input Object containing firstName, familyName, motherName (unused here but kept for API), and birthDate
 * @returns Comprehensive object with all sub-calculation results
 */
export const calculateNumerology = (input: { firstName: string; motherName: string; familyName: string; birthDate: string }) => {
  const dateParts = (input.birthDate || '').split(/[/.-]/);
  const day = parseInt(dateParts[0]) || 0;
  const month = parseInt(dateParts[1]) || 0;
  const year = parseInt(dateParts[2]) || 0;

  const dayNumber = sumDigits(day);
  const monthNumber = sumDigits(month);
  const yearNumber = sumDigits(year);

  // Calculate total from ALL original digits in the date
  const allDigitsStr = (input.birthDate || '').replace(/[^0-9]/g, '');
  const allDigits = allDigitsStr.split('').map(Number);
  const totalFromAllDigits = allDigits.reduce((sum, digit) => sum + digit, 0);
  const totalNumber = sumDigits(totalFromAllDigits);

  const firstNumber = calculateNumerologicalValue(input.firstName);
  const familyNumber = calculateNumerologicalValue(input.familyName);
  const totalNameNumber = firstNumber + familyNumber;

  const fullName = `${input.firstName} ${input.familyName}`;
  const eheviNumber = calculateEhevi(fullName);
  const itzurimNumber = totalNameNumber - eheviNumber;

  // Internal sums calculations
  const eheviInnerSumFirst = calculateEhevi(input.firstName);
  const eheviInnerSumFamily = calculateEhevi(input.familyName);
  const itzurimInnerSumFirst = firstNumber - eheviInnerSumFirst;
  const itzurimInnerSumFamily = familyNumber - eheviInnerSumFamily;

  // Calculate inner sums (sum to single digit, then add)
  const fullInnerSum = sumDigits(firstNumber) + sumDigits(familyNumber);
  const eheviInnerSum = sumDigits(eheviInnerSumFirst) + sumDigits(eheviInnerSumFamily);
  const itzurimInnerSum = sumDigits(itzurimInnerSumFirst) + sumDigits(itzurimInnerSumFamily);
  const full2InnerSum = sumDigits(eheviNumber) + sumDigits(itzurimNumber);

  // Calculate outer sum (raw totals) and inner sum (reduced digits)
  const hamashaOuterSum = totalFromAllDigits + totalNameNumber;
  const hamashaInnerSum = sumDigits(totalNameNumber) + sumDigits(totalFromAllDigits);

  return {
    dayNumber,
    monthNumber,
    yearNumber,
    totalNumber,
    totalFromAllDigits,
    firstNumber,
    familyNumber,
    totalNameNumber,
    eheviNumber,
    itzurimNumber,
    // Internal sums
    eheviInnerSumFirst,
    eheviInnerSumFamily,
    itzurimInnerSumFirst,
    itzurimInnerSumFamily,
    fullInnerSum,
    eheviInnerSum,
    itzurimInnerSum,
    full2InnerSum,
    // Outer and inner combined sums
    hamashaOuterSum,
    hamashaInnerSum
  };
};

/**
 * Calculates the "Profile" (Profile Sign) by combining Inner and Surrounding signs.
 * Logic:
 * 1. Sum = Inner Sign Number + Surrounding Sign Number.
 * 2. Result is wrapped to 1-12 range (e.g., 14 -> 2).
 * 
 * @param innerNumber 1-12
 * @param surroundingNumber 1-12
 * @returns Object with profile number (1-12), sign name, and raw calculation info
 */
export const calculateProfile = (innerNumber: number, surroundingNumber: number) => {
  if (!innerNumber || !surroundingNumber) {
    return { profileNumber: 0, profileSign: "?", zodiacPosition: 0 };
  }
  const profileNumber = innerNumber + surroundingNumber;

  // Simplified: Use 1-12 consistently, reuse existing SIGNS mapping
  const zodiacPosition = ((profileNumber - 1) % 12) + 1; // Always results in 1-12
  const profileSign = SIGNS_MAP[zodiacPosition]; // 1=Aries, 2=Taurus, ..., 12=Pisces

  return {
    profileNumber,
    profileSign,
    zodiacPosition
  };
};

export const SYMBOL_TO_SIGN_NUMBER: Record<string, number> = {
  'a': 1, 's': 2, 'd': 3, 'f': 4, 'g': 5, 'h': 6, 'j': 7, 'k': 8, 'l': 9, 'v': 10, 'x': 11, 'c': 12
};

/**
 * Parses Swiss Ephemeris coordinate formatting to extract degrees, minutes, and Hebrew sign names.
 */
export function parsePlanetPosition(formatted: string): { signName: string; degrees: number; minutes: number } | null {
  if (!formatted) return null;
  const regexA = /(\d{1,2})°([asdfghjklvxc])(\d{2})'(\d{2})["']{1,2}/i;
  const regexB = /(\d{1,2})°(\d{2})'(\d{2})["']{1,2}([asdfghjklvxc])/i;

  const matchA = formatted.match(regexA);
  if (matchA) {
    const sym = matchA[2].toLowerCase();
    const signNum = SYMBOL_TO_SIGN_NUMBER[sym];
    return {
      signName: SIGNS_MAP[signNum] || 'לא ידוע',
      degrees: parseInt(matchA[1], 10),
      minutes: parseInt(matchA[3], 10)
    };
  }

  const matchB = formatted.match(regexB);
  if (matchB) {
    const sym = matchB[4].toLowerCase();
    const signNum = SYMBOL_TO_SIGN_NUMBER[sym];
    return {
      signName: SIGNS_MAP[signNum] || 'לא ידוע',
      degrees: parseInt(matchB[1], 10),
      minutes: parseInt(matchB[2], 10)
    };
  }

  return null;
}